<template>
	<div class="chat-rich-media-album" :style="albumStyle">
		<div v-for="(item, index) in normalizedItems" :key="mediaKey(item, index)"
			:class="['album-item', uploadStateClass(item)]"
			:style="itemStyle(index)" @click="onPreview(item)">
			<img v-if="item.kind === 'image'" class="album-image" :src="imageSrc(item)" loading="lazy" />
			<video v-else class="album-video" :src="item.videoUrl" :poster="item.coverUrl"
				:controls="showVideoControls" preload="metadata"></video>
			<div v-if="item.kind === 'video' && !showVideoControls" class="video-mask">
				<i class="el-icon-video-play"></i>
			</div>
			<div v-if="item.uploadStatus === 'uploading'" class="media-state-mask loading">
				<i class="el-icon-loading"></i>
			</div>
			<div v-else-if="item.uploadStatus === 'failed'" class="media-state-mask failed">
				<i class="el-icon-warning-outline"></i>
				<span>{{ item.uploadError || '上传失败' }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import { calculateAlbumLayout, buildPlaceholderLayout } from "../../utils/albumLayout";
import { ensureMediaSizes, getCachedMediaSize, getMediaCacheKey } from "../../utils/mediaSize";
import { resolveImageDisplayUrl } from "../../utils/richMessage";

export default {
	name: "ChatRichMediaAlbum",
	props: {
		items: {
			type: Array,
			default: () => []
		},
		rounded: {
			type: Boolean,
			default: true
		},
		maxWidth: {
			type: Number,
			default: 260
		},
		spacing: {
			type: Number,
			default: 2
		},
		showVideoControls: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			layout: {
				width: this.maxWidth,
				height: 0,
				items: []
			}
		};
	},
	computed: {
		normalizedItems() {
			return (this.items || []).map((item) => ({
				...item,
				kind: item && item.kind ? item.kind : (item && item.videoUrl ? "video" : "image")
			}));
		},
		albumStyle() {
			return `width:${this.maxWidth}px;height:${this.layout.height}px;border-radius:${this.rounded ? 12 : 0}px;`;
		}
	},
	watch: {
		items: {
			handler() {
				this.refreshLayout();
			},
			deep: true,
			immediate: true
		},
		maxWidth() {
			this.refreshLayout();
		}
	},
	methods: {
		mediaKey(item, index) {
			return getMediaCacheKey(item) || `${item.kind || "media"}-${index}`;
		},
		buildResolvedSizes() {
			return this.normalizedItems.map((item, index) => {
				const size = getCachedMediaSize(item);
				if (!size || !size.width || !size.height) {
					return null;
				}
				return {
					index,
					width: size.width,
					height: size.height
				};
			});
		},
		refreshLayout() {
			if (!this.normalizedItems.length) {
				this.layout = {
					width: this.maxWidth,
					height: 0,
					items: []
				};
				return;
			}
			this.applyLayout();
			ensureMediaSizes(this.normalizedItems).finally(() => {
				this.applyLayout();
			});
		},
		applyLayout() {
			const resolvedSizes = this.buildResolvedSizes();
			if (resolvedSizes.every(Boolean)) {
				this.layout = calculateAlbumLayout(resolvedSizes, this.maxWidth, this.spacing);
				return;
			}
			this.layout = buildPlaceholderLayout(this.normalizedItems.length, this.maxWidth, this.spacing);
		},
		itemStyle(index) {
			const item = this.layout.items[index];
			if (!item) {
				return "";
			}
			return `left:${item.left}px;top:${item.top}px;width:${item.width}px;height:${item.height}px;border-radius:${this.rounded ? 12 : 0}px;`;
		},
		uploadStateClass(item) {
			return item && item.uploadStatus ? `is-${item.uploadStatus}` : "";
		},
		imageSrc(item) {
			return resolveImageDisplayUrl(item);
		},
		onPreview(item) {
			if (item.uploadStatus && item.uploadStatus !== "success") {
				return;
			}
			if (item.kind === "image") {
				this.$emit("preview-image", item);
			}
		}
	}
}
</script>

<style scoped lang="scss">
.chat-rich-media-album {
	position: relative;
	max-width: 100%;
	border-radius: 12px;
	overflow: hidden;

	.album-item {
		position: absolute;
		overflow: hidden;
		border-radius: 12px;
		background: rgba(0, 0, 0, 0.05);

		&.is-failed {
			.album-image,
			.album-video {
				filter: grayscale(1);
				opacity: 0.45;
			}
		}
	}

	.album-image,
	.album-video {
		width: 100%;
		height: 100%;
		display: block;
		object-fit: cover;
		background: rgba(0, 0, 0, 0.04);
	}

	.video-mask {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 0, 0, 0.18);
		color: white;
		font-size: 28px;
		pointer-events: none;
	}

	.media-state-mask {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 3;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		gap: 8px;
		color: white;
		pointer-events: none;

		&.loading {
			background: rgba(0, 0, 0, 0.26);
			font-size: 26px;
		}

		&.failed {
			background: rgba(32, 32, 32, 0.48);
			font-size: 14px;
			font-weight: 600;
			text-align: center;
			padding: 10px;
			box-sizing: border-box;

			i {
				font-size: 24px;
			}

			span {
				line-height: 1.4;
			}
		}
	}
}
</style>
